import { api, type MailAccount } from "@/lib/api";
import { applyServerAccountLabels } from "@/mail/connectionLabels";
import { syncMailSignatures } from "@/mail/signatureSync";

export type MailProvider = {
  id: string;
  name?: string;
  brand?: string;
  oauthReady?: boolean;
  connectedAccountIds?: string[];
};

export type ConnectResponse = {
  ok?: boolean;
  url?: string;
  authorizeUrl?: string;
  authorizationUrl?: string;
  needsBrowser?: boolean;
  message?: string;
  setupRequired?: boolean;
};

export function connectUrl(payload: ConnectResponse): string | undefined {
  return payload.authorizationUrl || payload.authorizeUrl || payload.url;
}

export function mailConnected(account: MailAccount): boolean {
  return account.connected !== false;
}

export async function fetchMailAccounts(): Promise<MailAccount[]> {
  const payload = await api<{ accounts?: MailAccount[] } | MailAccount[]>("/api/mail/accounts");
  const accounts = Array.isArray(payload) ? payload : payload.accounts || [];
  applyServerAccountLabels(accounts);
  await syncMailSignatures(accounts);
  return accounts;
}

export async function fetchMailProviders(): Promise<MailProvider[]> {
  const payload = await api<{ providers?: MailProvider[] } | MailProvider[]>(
    "/api/mail/providers",
  ).catch(() => [] as MailProvider[]);
  return Array.isArray(payload) ? payload : payload.providers || [];
}

export type GoogleConnectServices = {
  mail: boolean;
  calendar: boolean;
  chat: boolean;
  meetings: boolean;
};

export async function startGmailConnect(opts?: {
  email?: string;
  includeCalendar?: boolean;
  services?: GoogleConnectServices;
}): Promise<ConnectResponse> {
  const body: Record<string, unknown> = {};
  if (opts?.email) body.email = opts.email;
  if (opts?.services) {
    body.services = opts.services;
    body.includeCalendar = opts.services.calendar;
  } else if (opts?.includeCalendar) body.includeCalendar = true;

  return api<ConnectResponse>("/api/mail/providers/gmail/connect", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function disconnectMailAccount(accountId: string): Promise<{ message?: string }> {
  return api<{ message?: string }>(
    `/api/mail/accounts/${encodeURIComponent(accountId)}/disconnect`,
    { method: "POST" },
  );
}

export async function disconnectCalendarAccount(
  accountId: string,
): Promise<{ message?: string }> {
  return api<{ message?: string }>(
    `/api/mail/accounts/${encodeURIComponent(accountId)}/calendar/disconnect`,
    { method: "POST" },
  );
}
